import { useRef, useState, type FormEvent } from 'react';

/**
 * The code as typed, sent once per distinct six digits and never while an attempt runs. An attempt
 * that ended in a problem (a wrong code, no connection, Paige down) is over: the same digits go
 * again when the button is pressed, so the button is never one that does nothing. That is read from
 * what is on screen now (not busy, a problem said), not from an effect that runs a moment later.
 */
export function useCodeEntry(
  busy: boolean,
  error: string | null,
  onVerify: (code: string) => void,
) {
  const [code, setCode] = useState('');
  const sent = useRef<string | null>(null);
  const over = !busy && error !== null;
  const send = (value: string): void => {
    if (value.length !== 6 || busy) return;
    if (sent.current === value && !over) return;
    sent.current = value;
    onVerify(value);
  };
  const submit = (e: FormEvent): void => {
    e.preventDefault();
    send(code);
  };
  const change = (value: string): void => {
    setCode(value);
    if (value !== sent.current) sent.current = null;
  };
  return { code, change, send, submit };
}
